import endpoints from "../config/endpoints.json";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";
import axios from "axios";
import type { ApiResponse } from "../types";
import { convertFormDataImageFilesToWebp } from "../utilization/StorageUtilization";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
    ? connection_development
    : process.env.REACT_APP_NODE_ENV === "production"
    ? connection_production
    : connection_local;

/**
 * Storage files
 */
export const queryBrandFileDTOByIdList = async (params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.storage.url}${endpoints.storage.context}${endpoints.storage["brand-files"].query.dto["by-idList"].context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryBrandFileListByFrameworkAnnualPlanWeekItemList = async (
  params
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.storage.url}${endpoints.storage.context}${endpoints.storage["brand-files"].query["by-frameworkAnnualPlanWeekItemList"].context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

// Answer uploads (a phone photo over cellular) had no timeout at all — axios's default is none,
// so a stalled connection kept the learner on "yükleniyor" forever. Past this the request is
// aborted and surfaces as the ordinary `{ err }` → the caller's FAILED state with a retry.
export const STORAGE_UPLOAD_TIMEOUT_MS = 90000;
// Slowest upload rate the timeout still has to accommodate (~400 kbps, a weak home ADSL uplink). A video
// or scanned PDF answer (up to 25 MB, never compressed) needs minutes at that rate, so a flat 90 s would
// abort it on every retry; the floor stays 90 s for a compressed photo.
const MIN_UPLOAD_BYTES_PER_SECOND = 50000;

export const uploadTimeoutFor = (formData): number => {
  const file = formData && typeof formData.get === "function" ? formData.get("file") : null;
  const size = file && typeof file.size === "number" ? file.size : 0;
  return Math.max(STORAGE_UPLOAD_TIMEOUT_MS, Math.ceil(size / MIN_UPLOAD_BYTES_PER_SECOND) * 1000);
};

// Unified upload — formData MUST carry sourceType + sourceId + subSourceId. All three are
// required @RequestParams on the backend (a missing subSourceId is a 400, not a null), and
// together with (userId, userProfileId) they form the row's upsert key.
export const createOrUpdateStorageFileToUserProfile = async (formData): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.storage.url}${endpoints.storage.userProfile.context}${endpoints.storage.userProfile.upload.file.context}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: uploadTimeoutFor(formData),
      }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const createOrUpdateStorageFileToUserProfileFromMobile = async (
  formData
): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.storage.url}${endpoints.storage.userProfile.context}${endpoints.storage.userProfile.upload.file["from-mobile"].context}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: uploadTimeoutFor(formData),
      }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

// Chat uploads (the mascot chat's drawings, voice recordings and TTS replies) go through the
// unified user-profile upload. Their dedicated `by-activityTemplateChat…` / `by-subjectDescription…`
// endpoints were removed from the storage service (catchup-parent 3e7841451, Apr 2026), so posting
// there answered 404 and every drawing failed with "Failed to upload image". The callers still
// describe the chat in its own terms; this maps that onto the unified source key:
//   activity chat           → ACTIVITY_TEMPLATE_CHAT   · sourceId = activityId          · subSourceId = activityTemplateId
//   learning-path anlatım   → SUBJECT_DESCRIPTION_CHAT · sourceId = subjectDescriptionId · subSourceId = 0
// The row is upserted on that key, so a later upload in the same chat re-points the row — but each
// upload is written under a fresh file name, so the URLs already in the conversation keep resolving.
const uploadChatFileToUserProfile = async (
  formData: FormData,
  sourceType: "ACTIVITY_TEMPLATE_CHAT" | "SUBJECT_DESCRIPTION_CHAT",
  sourceId: FormDataEntryValue | null,
  subSourceId: FormDataEntryValue | null
): Promise<ApiResponse> => {
  try {
    // Chat images are canvas drawings (PNG) fed to a vision LLM — re-encode to WebP at high
    // quality before upload. The graded open-ended answer flow (createOrUpdateStorageFileToUserProfile)
    // is deliberately untouched.
    await convertFormDataImageFilesToWebp(formData);
    formData.set("sourceType", sourceType);
    formData.set("sourceId", String(sourceId ?? ""));
    formData.set("subSourceId", String(subSourceId ?? "0"));
    const response = await axios.post(
      `${connection.base.storage.url}${endpoints.storage.userProfile.context}${endpoints.storage.userProfile.upload.file.context}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const createOrUpdateStorageFileFromActivityTemplateChatAndActivityTemplateChatItem = async (
  formData
): Promise<ApiResponse> =>
  uploadChatFileToUserProfile(
    formData,
    "ACTIVITY_TEMPLATE_CHAT",
    formData.get("activityId"),
    formData.get("activityTemplateId")
  );

export const createOrUpdateStorageFileFromSubjectDescriptionAndSubjectDescriptionChatAndSubjectDescriptionChatItem =
  async (formData): Promise<ApiResponse> =>
    uploadChatFileToUserProfile(
      formData,
      "SUBJECT_DESCRIPTION_CHAT",
      formData.get("subjectDescriptionId"),
      null
    );
